import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { Card, Row, Col, Button, Input, Modal, Skeleton, Tabs, Space, DatePicker, Tooltip, Tour, Popconfirm, Table } from "antd";
import dayjs from "dayjs";
import Select from "react-select";
import { faInfoCircle, faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import WindowedSelect from "react-windowed-select";

let BASE_STATE_TASK = {
  description: null,
  comments: "",
  item: null,
  items_xref: null,
  task_status: null,
  task_statuses_xref: null,
  due_at: null,
  form_due_date: null,
  jobs_xref: null,
  owned_by: null,
  date_in: dayjs(),
};

/*
Prop api
open: boolean, 
client_id: int,
refresh: function
recurring: boolean
*/

const tour_columns = [
  {
    title: "Desired Output",
    dataIndex: "output",
    key: "output",
  },
  {
    title: "Input for Task Name",
    dataIndex: "input",
    key: "input",
  },
];
const tour_data = [
  { input: "Bookkeeping for {{MMMM}}", output: "Bookkeeping for January" },
  { input: "Bookkeeping - {{MM/YY}}", output: "Bookkeeping - 01/23" },
  { input: "{{dddd}}'s Bookkeeping Task", output: "Monday's Bookkeeping Task" },
];

function ModalAddTask(props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalWidth, setModalWidth] = useState(1200);

  const [task, setTask] = useState(BASE_STATE_TASK);

  const [company, setCompany] = useState(null);
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [tourOpen, setTourOpen] = useState(false);

  const focusFirst = useRef(null);
  const tourFocus = useRef(null);

  const GetCompany = async () => {
    let response = await props.AxiosRequest("/api/companies/get");
    setCompany(response.data.company);
  };
  const GetClients = async () => {
    let response = await props.AxiosRequest("/api/clients/get/all");
    let mapped_clients = response.data.clients.map((m) => {
      return { ...m, value: m.id, label: m.name };
    });
    setClients(mapped_clients);
  };
  const GetJobs = async () => {
    let response = await props.AxiosRequest("/api/jobs/get", { client_id: props.addTaskProps?.client_id });
    let mapped_jobs = response.data.jobs.map((m) => {
      return { ...m, value: m.id, label: m.name };
    });
    setJobs(mapped_jobs);
  };
  const GetTaskStatuses = async () => {
    let response = await props.AxiosRequest("/api/task-statuses/get");
    let statuses = response.data.task_statuses.map((m) => {
      return { ...m, label: m.name, value: m.id };
    });
    setTaskStatuses(statuses);
  };
  const GetUsers = async () => {
    let response = await props.AxiosRequest("/api/users/get");
    let mapped_users = response.data.users.map((m) => {
      return { ...m, value: m.id, label: m.name };
    });
    setUsers(mapped_users);
  };
  const GetItems = async () => {
    let response = await props.AxiosRequest("/api/items/get");
    let mapped_items = response.data.items.map((m) => {
      return { ...m, value: m.id, label: m.name };
    });
    setItems(mapped_items);
  };
  const AddTask = async () => {
    if (task?.description?.length === 0) return props.MakeNotification("info", "Please enter the task name.");
    if (!task.items_xref || !task.description || !task.task_statuses_xref || !task.due_at || !task.jobs_xref || !task.owned_by)
      return props.MakeNotification("info", "Please fill in all required fields.");
    let response = await props.AxiosRequest("/api/tasks/insert", { ...task });
    props.setAddTaskProps({ ...props.addTaskProps, open: false, refresh: true, new_task_id: response.data.new_task_id });
  };
  const AddRecurringTask = async () => {
    if (task?.description?.length === 0) return props.MakeNotification("info", "Please enter the task name.");
    if (!task.items_xref || !task.description || !task.next_scheduled_at || !task.due_at || !task.jobs_xref || !task.owned_by || !task.frequency)
      return props.MakeNotification("info", "Please fill in all required fields.");
    await props.AxiosRequest("/api/recurring-tasks/insert", { ...task });
    props.setAddTaskProps({ ...props.addTaskProps, open: false, refresh: true });
  };
  const OpenModalPrep = async () => {
    let found_client = null,
      found_job = null,
      start_status = null,
      current_user = null,
      found_item = null;

    found_client = clients.find((f) => f.id === props.addTaskProps?.client_id);
    found_job = jobs.find((f) => f.id === props.addTaskProps?.job_id);
    start_status = taskStatuses.find((f) => f.start === 1);
    current_user = users.find((f) => f.current_user === 1);
    if (found_job) found_item = items.find((f) => f.id === found_job.items_xref);
    setTask({
      ...task,
      comments: "",
      description: found_job ? found_job?.name + " - Task" : "Task",
      jobs_xref: found_job?.id,
      job: found_job,
      items_xref: found_item?.id,
      item: found_item,
      task_status: start_status,
      task_statuses_xref: start_status?.id,
      owned_by: current_user?.id,
      user: current_user,
      client: found_client,
      clients_xref: found_client?.id,
      due_at: props.addTaskProps?.recurring ? null : dayjs().add(company?.default_task_days_due, "days"),
    });
  };

  const tour_steps = [
    {
      title: "When to use custom task names?",
      description: (
        <div>
          Commonly, cusomized task names are useful when you have a task that is recurring. <br />
          For example, if you have a bookkeeping job that you do every month, you probably want your tasks to be named with the month in the name of the task, so that when you go back you know which
          task was for which month.
        </div>
      ),
      target: null,
    },
    {
      title: "How do I do that?",
      description: (
        <>
          In our example, we want to put the month in our task. Here are some examples of how to accomplish that: <br />
          <Table columns={tour_columns} dataSource={tour_data} pagination={false} />
          <br />
          All these options give better task names, and are completely customizable!
        </>
      ),
      placement: "right",
      target: () => tourFocus?.current,
    },
    {
      title: "Need more customization?",
      description: (
        <>
          With that example in mind, you can have the task names be customized to your liking. When working with the formatting, we use the formating described{" "}
          <Link target="_blank" to="https://day.js.org/docs/en/display/format">
            here
          </Link>
          . Make sure to enclose any of the formatting in the double curly braces, and you're set!
          <br />
        </>
      ),
      placement: "right",
      target: () => tourFocus?.current,
    },
  ];

  useEffect(() => {
    OpenModalPrep();
    setModalOpen(props.addTaskProps?.open);
  }, [props.addTaskProps?.open, jobs]);

  useEffect(() => {
    GetJobs();
  }, [props.addTaskProps?.client_id, props.addTaskProps?.job_id]);

  useEffect(() => {
    GetClients();
    GetItems();
    GetJobs();
    GetUsers();
    GetTaskStatuses();
    GetCompany();
  }, []);

  return (
    <>
      <Modal
        afterOpenChange={(open) => {
          open && focusFirst.current?.select();
        }}
        title="Add A New Task"
        centered
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          props.setAddTaskProps({ ...props.addTaskProps, open: false, client_id: null });
        }}
        footer={
          <>
            <Button
              onClick={() => {
                setModalOpen(false);
                props.setAddTaskProps({ ...props.addTaskProps, open: false, client_id: null });
              }}
            >
              Cancel
            </Button>
            {props.addTaskProps?.recurring ? (
              <Button type="primary" onClick={AddRecurringTask}>
                Add Recurring Task
              </Button>
            ) : (
              <Button type="primary" onClick={AddTask}>
                Add Task
              </Button>
            )}
          </>
        }
        width={modalWidth}
      >
        {task ? (
          <Row gutter={[{ xs: 8, sm: 16, md: 16, lg: 16 }, 8]}>
            <Col span={8}>
              <label className="col-form-label-sm">Client *</label>
              <WindowedSelect
                value={task.client}
                options={props.MakeClientOptions(clients)}
                onChange={(e) => {
                  setTask({ ...task, client: e, clients_xref: e.value });
                }}
              />
            </Col>
            <Col span={8}>
              <label className="col-form-label-sm">Job *</label>
              <Select
                value={task.job}
                options={props.MakeJobOptions(jobs)}
                onChange={(e) => {
                  let found_item = items.find((f) => f.id === e.items_xref);
                  setTask({ ...task, job: e, jobs_xref: e.value, items_xref: found_item?.id, item: found_item });
                }}
              />
            </Col>
            <Col span={8}></Col>
            <Col span={8} ref={tourFocus}>
              <label className="col-form-label-sm">Task Name *</label>
              {props?.addTaskProps?.recurring ? (
                <Popconfirm
                  icon={
                    <FontAwesomeIcon
                      icon={faQuestionCircle}
                      className="mr-2"
                      style={{
                        color: "blue",
                      }}
                    />
                  }
                  title="Want customized task names?"
                  description="If you want to learn more about customizing your task names, click the learn button below."
                  okText="Learn More"
                  onConfirm={() => {
                    setTourOpen(true);
                  }}
                  cancelText="Nevermind"
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="ml-2 clickable" />
                </Popconfirm>
              ) : null}
              <Input
                ref={focusFirst}
                value={task.description}
                placeholder="Bookkeeping, 1040, Quarterly Report, etc"
                onChange={(e) => {
                  setTask({ ...task, description: e.target.value });
                }}
              />
            </Col>
            <Col span={8}>
              <label className="col-form-label-sm">Item *</label>
              <Select
                value={task.item}
                options={items}
                onChange={(e) => {
                  setTask({ ...task, item: e, items_xref: e.value });
                }}
              />
            </Col>
            <Col span={8}>
              <label className="col-form-label-sm">Assignee *</label>
              <Select
                value={task.user}
                options={props.MakeUserOptions(users)}
                onChange={(e) => {
                  setTask({ ...task, user: e, owned_by: e.value });
                }}
              />
            </Col>
            <Col span={24}>
              <label className="col-form-label-sm">Comments</label>
              <Input.TextArea
                value={task.comments}
                onChange={(e) => {
                  setTask({ ...task, comments: e.target.value });
                }}
              />
            </Col>
            {!props.addTaskProps?.recurring ? (
              <Col span={6}>
                <label className="col-form-label-sm">Task Status *</label>
                <Select
                  value={task.task_status}
                  options={taskStatuses}
                  onChange={(e) => {
                    setTask({ ...task, task_status: e, task_statuses_xref: e.value });
                  }}
                />
              </Col>
            ) : null}
            {props.addTaskProps?.recurring ? (
              <Col span={6}>
                <label className="col-form-label-sm">Next Scheduled Date *</label>
                <DatePicker
                  format={"MM/DD/YYYY"}
                  style={{ width: "100%" }}
                  allowClear={false}
                  value={task.next_scheduled_at}
                  onChange={(e) => {
                    setTask({ ...task, next_scheduled_at: e });
                  }}
                />
              </Col>
            ) : null}
            {!props?.addTaskProps?.recurring ? (
              <Col span={6}>
                <label className="col-form-label-sm">Date In *</label>
                <DatePicker
                  format={"MM/DD/YYYY"}
                  style={{ width: "100%" }}
                  allowClear={false}
                  value={task.date_in}
                  onChange={(e) => {
                    setTask({ ...task, date_in: e });
                  }}
                />
              </Col>
            ) : null}
            <Col span={6}>
              <label className="col-form-label-sm">{props?.addTaskProps?.recurring ? "Next " : null}Due Date *</label>
              <DatePicker
                format={"MM/DD/YYYY"}
                style={{ width: "100%" }}
                allowClear={false}
                value={task.due_at}
                onChange={(e) => {
                  setTask({ ...task, due_at: e });
                }}
              />
            </Col>
            {!props.addTaskProps?.recurring ? (
              <Col span={6}>
                <label className="col-form-label-sm">Form Due Date</label>
                <DatePicker
                  format={"MM/DD/YYYY"}
                  style={{ width: "100%" }}
                  value={task.form_due_date}
                  onChange={(e) => {
                    setTask({ ...task, form_due_date: e });
                  }}
                />
              </Col>
            ) : null}
            {props.addTaskProps?.recurring ? (
              <Col span={6}>
                <label className="col-form-label-sm">Frequency *</label>
                <Select
                  value={task.frequency_object}
                  options={[
                    { value: "annually", label: "Annually" },
                    { value: "quarterly", label: "Quarterly" },
                    { value: "monthly", label: "Monthly" },
                    { value: "weekly", label: "Weekly" },
                    { value: "biweekly", label: "Biweekly" },
                  ]}
                  onChange={(e) => {
                    setTask({ ...task, frequency: e.value, frequency_object: e });
                  }}
                />
              </Col>
            ) : null}
          </Row>
        ) : (
          <Skeleton active />
        )}
      </Modal>
      <Tour open={tourOpen} onClose={() => setTourOpen(false)} steps={tour_steps} />
    </>
  );
}

export default ModalAddTask;
